/**
 * ScreenshotDrop.jsx - drop a picture of a roster, get the ladder filled in.
 *
 * 🔴 THE READ IS A DRAFT, NOT AN ANSWER. Everything below exists to keep that
 * true. The rows land in the ordinary ladder, where they can be retyped like
 * any other row; the ones the matcher was unsure about say so; the lines it
 * could not read at all are printed rather than guessed; and one tap puts the
 * roster back exactly as it was. A reader that silently produced a wrong price
 * would produce a wrong grade the user has no way to spot, which is a worse
 * failure than reading nothing.
 *
 * 🔴 SEVERAL PICTURES ARE THE NORMAL CASE. Sixteen slots do not fit on a phone
 * screen. The control therefore stays visible after a successful read and says
 * so, and ladderFill handles the overlap and the re-placement. Nothing here
 * assumes one image is the whole roster.
 *
 * 🔴 THE IMAGE NEVER LEAVES THE DEVICE. Recognition runs in the browser through
 * a dynamically imported engine; only the rows the user then chooses to submit
 * ever reach a server, through the same explicit tap that already existed. This
 * is why the copy can say what it says, and it must stay true.
 */

import { useRef, useState } from "react";
import { theme } from "../../data/theme.js";
import { C, SZ, tagPill } from "../Submit/submitStyles.js";
import {
  WRONG_SCREEN_HELP,
  fillLadderFromRead,
  looksLikeWrongScreen,
  readSummary,
  undoRead,
} from "../../utils/ladderFill.js";
import { READER_VERSION } from "../../utils/readerVersion.js";
import { captureRateScreenshotUsed } from "../../utils/rateAnalytics.js";

const ACCEPT = ".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp";

/**
 * 🔴 WHICH SCREEN TO PHOTOGRAPH, SAID OUT LOUD, BECAUSE THE WRONG ONE CANNOT BE
 * RESCUED. Sep 1 2026: Ben pointed the reader at Yahoo's weekly lineup tab and
 * it returned eight players, sixty-seven unreadable lines, and two men who were
 * not on the roster at all. That page has no prices on it, so no amount of
 * work on the reader can make it produce a priced roster. Ben: "We need to make
 * sure people know where to take the screenshot then."
 *
 * 🔴 ONLY THE PATH THAT HAS BEEN TESTED IS NAMED. Yahoo's is verified end to
 * end. Sleeper's and ESPN's are not, and a confidently wrong menu path would
 * send people to another priceless screen with the app's own instructions
 * behind it, which is worse than saying nothing. So everything else is
 * described by SHAPE - a list with a price against every player - which is the
 * thing that actually has to be true.
 */
export const WHERE_TO_SHOOT = [
  "Yahoo: League → Draft → Draft Results, then the Teams tab.",
  "Anywhere else: the page that shows what each player cost.",
  "A weekly lineup page will not work; it has no prices on it.",
  "Use a scrolling screenshot, or send the roster in two pictures.",
];

/** A phone photo of a screen can be enormous; the canvas step downscales, but
 *  refuse the truly absurd before spending seconds on it. */
const MAX_BYTES = 25 * 1024 * 1024;

const card = {
  border: `1px dashed ${C.border.default}`,
  borderRadius: theme.radius.lg,
  background: C.bg.deep,
  padding: 14,
  marginBottom: 10,
};

export default function ScreenshotDrop({ positions, ladder, onLadder }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [note, setNote] = useState(null);
  const [error, setError] = useState(null);
  const [unread, setUnread] = useState([]);
  // The last read, kept whole so it can be taken back. See ladderFill's note on
  // why the undo is a snapshot rather than a list of edits.
  const [lastRead, setLastRead] = useState(null);
  const [seen, setSeen] = useState([]);
  const [showSeen, setShowSeen] = useState(false);
  // Set when the read looks like the wrong PAGE rather than a bad picture. The
  // two need different advice and produce the same half-filled form.
  const [wrongScreen, setWrongScreen] = useState(false);

  async function handleFile(file) {
    if (!file) return;
    setError(null);
    if (file.size > MAX_BYTES) {
      setError("That image is very large. Try a screenshot rather than a photo.");
      // 🔴 A REFUSED FILE IS A FAILED USE, and it has to count as one: an
      // `ok` rate computed only over images we agreed to read would say the
      // reader works better than it does for the people it turns away.
      captureRateScreenshotUsed({ ok: false });
      return;
    }
    setBusy(true);
    setProgress(0);
    setNote(null);
    setWrongScreen(false);
    try {
      // Loaded here and nowhere else, so a visitor who never uploads anything
      // never downloads a recognition engine.
      const { readRoster } = await import("../../utils/screenshotRead.js");
      const { entries, unreadable, seen: sawRows } = await readRoster(file, setProgress);
      setSeen(sawRows || []);
      const out = fillLadderFromRead(entries, positions, ladder);
      const off = looksLikeWrongScreen({ ...out, unreadable });
      if (out.placed.length === 0) {
        setError(
          off
            ? WRONG_SCREEN_HELP
            : "I could not find any players in that one. A screenshot of the roster list works better than a photo of a screen."
        );
        setUnread([]);
        setLastRead(null);
        setShowSeen(true);   // nothing landed, so show the evidence unprompted
        captureRateScreenshotUsed({ ok: false });
      } else {
        // 🔴 A WRONG-SCREEN READ STILL FILLS THE FORM, AND STILL SAYS SO. The
        // rows are placed because the undo is one tap and half a roster is
        // occasionally what somebody wanted; the warning is shown because
        // "check the highlighted rows" is useless advice for a page that was
        // never going to work, and eight guesses left standing quietly become a
        // wrong grade.
        onLadder(out.ladder);
        setLastRead(out);
        setUnread(unreadable || []);
        setNote(readSummary({ ...out, unreadable }));
        setWrongScreen(off);
        // 🔴 `ok` IS "SOMETHING LANDED", not "everything landed" and not "the
        // right screen". A wrong-screen read that still placed rows saved the
        // user typing, which is the only promise this control makes; the count
        // of rows and the players in them are a roster and never travel.
        captureRateScreenshotUsed({ ok: true });
      }
    } catch (e) {
      // 🔴 A failed read lands on the plain form, never on an error screen. The
      // ladder still works; the user has simply learned this image did not.
      setError("I could not read that image. You can still type the roster in below.");
      captureRateScreenshotUsed({ ok: false });
    } finally {
      setBusy(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  function takeBack() {
    if (!lastRead) return;
    onLadder(undoRead(lastRead));
    setLastRead(null);
    setNote(null);
    setUnread([]);
    setError(null);
    setWrongScreen(false);
  }

  return (
    <div style={card} data-testid="screenshot-drop">
      {/* 🔴 A TITLE, AND THE TAG THAT BELONGS BESIDE IT (Ben, Sep 3 2026). This
          panel had no heading at all - the button was the title - so there was
          nowhere to say what is true of it: reading a roster out of a picture is
          the least reliable thing on this page, and a reader deserves to know
          that before they spend an upload on it rather than after a wrong row.
          The pill is monochrome on BuildChip's rule: a tag names, it does not
          rank, and the grade palette stays out of it. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: SZ.sub, fontWeight: 800, color: C.text.primary, lineHeight: 1.3 }}>
          Fill from a screenshot
        </span>
        <span data-testid="screenshot-beta" style={tagPill}>
          Beta
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => inputRef.current && inputRef.current.click()}
          disabled={busy}
          data-testid="screenshot-pick"
          style={{
            padding: "8px 14px",
            fontSize: SZ.small,
            fontWeight: 800,
            borderRadius: theme.radius.lg,
            border: `1px solid ${C.brand.accent}`,
            background: "transparent",
            color: busy ? C.text.faint : C.brand.accent,
            cursor: busy ? "default" : "pointer",
          }}
        >
          {/* The title above names the feature, so the button names the action
              rather than repeating it. */}
          {busy ? "Reading…" : lastRead ? "Add another image" : "Choose an image"}
        </button>
        <div style={{ fontSize: SZ.micro, color: C.text.faint, lineHeight: 1.4, minWidth: 0 }}>
          {busy
            ? /* Several seconds of nothing reads as broken. */
              `Reading the image… ${Math.round((progress || 0) * 100)}%`
            : "Your roster from Sleeper, ESPN, Yahoo or anywhere else. More than one picture is fine. The image stays on your device."}
        </div>
      </div>

      {/* Always on, never behind a "show me how". The wrong screen costs a
          person a whole upload and a wrong roster, and a line of text is
          cheaper than either. */}
      <ul
        data-testid="screenshot-where"
        style={{
          margin: "8px 0 0",
          paddingLeft: 18,
          fontSize: SZ.micro,
          color: C.text.faint,
          lineHeight: 1.6,
        }}
      >
        {WHERE_TO_SHOOT.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        onChange={(e) => handleFile(e.target.files && e.target.files[0])}
        style={{ display: "none" }}
        data-testid="screenshot-input"
      />

      {error && (
        <div
          data-testid="screenshot-error"
          style={{ marginTop: 10, fontSize: SZ.small, color: C.status.warning, lineHeight: 1.5 }}
        >
          {error}
        </div>
      )}

      {wrongScreen && !error && (
        <div
          data-testid="screenshot-wrong-screen"
          style={{
            marginTop: 10,
            padding: 10,
            borderRadius: theme.radius.lg,
            border: `1px solid ${C.status.warning}`,
            fontSize: SZ.small,
            color: C.status.warning,
            lineHeight: 1.5,
          }}
        >
          {WRONG_SCREEN_HELP}
        </div>
      )}

      {note && !error && (
        <div style={{ marginTop: 10 }}>
          <div
            data-testid="screenshot-note"
            style={{ fontSize: SZ.small, color: C.text.secondary, lineHeight: 1.5 }}
          >
            {note}
          </div>

          {unread.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: SZ.micro, color: C.text.faint, marginBottom: 4 }}>
                {/* Printed rather than guessed. A line the reader could not
                    resolve fills nothing, so the user can see the hole. */}
                Lines I could not match to a player:
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {unread.slice(0, 8).map((u, i) => (
                  <span
                    key={`${u.raw}-${i}`}
                    style={{
                      fontSize: SZ.micro,
                      padding: "2px 8px",
                      borderRadius: theme.radius.lg,
                      border: `1px solid ${C.border.default}`,
                      color: C.text.faint,
                    }}
                  >
                    {u.raw}
                    {u.price != null ? ` $${u.price}` : ""}
                  </span>
                ))}
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={() => setShowSeen((v) => !v)}
            data-testid="screenshot-seen-toggle"
            style={{
              marginTop: 8,
              marginRight: 8,
              padding: "4px 10px",
              fontSize: SZ.micro,
              fontWeight: 700,
              borderRadius: theme.radius.lg,
              border: `1px solid ${C.border.default}`,
              background: "transparent",
              color: C.text.faint,
              cursor: "pointer",
            }}
          >
            {showSeen ? "Hide what I read" : "Show what I read"}
          </button>
          <button
            type="button"
            onClick={takeBack}
            data-testid="screenshot-undo"
            style={{
              marginTop: 8,
              padding: "4px 10px",
              fontSize: SZ.micro,
              fontWeight: 700,
              borderRadius: theme.radius.lg,
              border: `1px solid ${C.border.default}`,
              background: "transparent",
              color: C.text.secondary,
              cursor: "pointer",
            }}
          >
            Undo this screenshot
          </button>
        </div>
      )}

      {showSeen && seen.length > 0 && (
        <div style={{ marginTop: 10 }} data-testid="screenshot-seen">
          <div style={{ fontSize: SZ.micro, color: C.text.faint, marginBottom: 4 }}>
            Every line the reader found, in the order it found them ({READER_VERSION}):
          </div>
          <div
            style={{
              maxHeight: 220,
              overflowY: "auto",
              border: `1px solid ${C.border.default}`,
              borderRadius: theme.radius.lg,
              padding: 8,
            }}
          >
            {seen.map((line, i) => (
              <div
                key={`${line}-${i}`}
                style={{
                  fontSize: SZ.micro,
                  color: C.text.secondary,
                  fontFamily: "monospace",
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {String(i + 1).padStart(2, "0")}  {line}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
